/**
 * Meeting Detail Page - OZK 모임 상세 페이지 (첫 화면)
 * 기획안: docs/OZK_MEETING_DETAIL_PAGE_PLAN.md
 * 레이아웃: 당근마켓 모임 상세 페이지 스타일
 *
 * 구성:
 * - 상단 헤더 (고정)
 * - 좌측 사이드바 (데스크톱) + 메인 콘텐츠
 * - 하단 고정바 (모바일)
 */

import { AuthDialog } from "@/components/AuthDialog";
import { MeetingBottomBar } from "@/components/meetings/MeetingBottomBar";
import {
  MeetingSidebar,
  boardCategories,
} from "@/components/meetings/MeetingSidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShareKakaoButton } from "@/components/ShareKakaoButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { getIdToken } from "@/lib/firebase";
import { normalizeImageUrl } from "@/lib/image-url";
import { getAvatarUrl, getDisplayName } from "@/utils/displayName";
import { meetingCategories } from "@shared/schema";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  ArrowLeft,
  ChevronRight,
  MessageCircle,
  MoreVertical,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";

interface MeetingDetailData {
  id: number;
  title: string;
  description: string;
  category: string;
  ageRange?: string | null;
  state: string;
  city: string;
  suburb: string;
  location: any;
  schedule: any;
  nextMeetingDate: string | null;
  meetingTime: string | null;
  capacity: number | null;
  currentMembers: number;
  fee: number | null;
  kakaoUrl: string | null;
  kakaoClicks: number;
  coverImage: string | null;
  photos: string[];
  hostId: string;
  hostName: string;
  hostEmail: string | null;
  hostNickname: string | null;
  hostDisplayName: string | null;
  hostProvider: string | null;
  status: string;
  isPremium: boolean;
  premiumUntil: string | null;
  views: number;
  postCount: number;
  createdAt: string;
  updatedAt: string | null;
  members: {
    total: number;
    preview: Array<{
      id: string;
      nickname: string;
      displayName: string;
      avatarUrl: string | null;
      role: "leader" | "assistant" | "member";
      location?: string;
      state?: string | null; // Phase 1: 지역 정보 (NSW, QLD 등)
      suburb?: string | null; // Phase 1: 동네 정보 (시드니, 멜버른 등)
      mbti?: string; // Phase 2에서 추가 예정
      badges: string[];
      bio?: string;
    }>;
  };
  events: Array<{
    id: string;
    title: string;
    date: string;
    time: string | null;
    location: any;
    capacity: number | null;
    attendees: number;
    status: "open" | "closed";
    image: string | null;
    isPrivate?: boolean;
  }>;
  posts: Array<{
    id: string;
    title?: string;
    content: string;
    images: string[];
    authorNickname: string;
    authorLocation?: string;
    likes: number;
    comments: number;
    createdAt: string;
    category: string;
  }>;
  stats: {
    totalEvents: number;
    monthlyParticipants: number;
    totalPosts: number;
  };
}

export default function MeetingDetail() {
  const [match, params] = useRoute<{ id: string }>("/meetings/:id");
  const [, setLocation] = useLocation();
  const navigate = setLocation;
  const { user } = useAuth();
  const breakpoint = useBreakpoint();
  const queryClient = useQueryClient();
  const isMobile = breakpoint === "mobile";
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedPostCategory, setSelectedPostCategory] = useState("all");
  const [viewTracked, setViewTracked] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const meetingId = params?.id;

  // 섹션 참조 (스크롤용)
  const albumSectionRef = useRef<HTMLDivElement>(null);
  const membersSectionRef = useRef<HTMLDivElement>(null);
  const eventsSectionRef = useRef<HTMLDivElement>(null);
  const postsSectionRef = useRef<HTMLDivElement>(null);

  // 섹션으로 스크롤하는 함수
  const scrollToSection = (sectionId: string) => {
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {
      album: albumSectionRef,
      members: membersSectionRef,
      events: eventsSectionRef,
      posts: postsSectionRef,
    };

    const ref = refs[sectionId];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!user || !meetingId) {
      setIsJoined(false);
      return;
    }

    const checkMembership = async () => {
      try {
        const token = await getIdToken();
        if (!token) return;
        const res = await fetch(`/api/meetings/${meetingId}/membership`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) return;
        const result = await res.json();
        const payload = result.data ?? result;
        if (!cancelled) {
          setIsJoined(Boolean(payload?.isMember));
        }
      } catch (error) {
        console.error("Failed to check membership:", error);
      }
    };

    checkMembership();

    return () => {
      cancelled = true;
    };
  }, [user, meetingId, getIdToken]);

  const { data: meetingData, isLoading } = useQuery<{
    ok: boolean;
    data: MeetingDetailData;
  }>({
    queryKey: ["/api/meetings", meetingId],
    queryFn: async () => {
      const res = await fetch(`/api/meetings/${meetingId}`);
      if (!res.ok) throw new Error("Failed to fetch meeting details");
      const json = await res.json();

      // API 응답이 중첩 구조일 수 있음: { ok: true, data: { ok: true, data: {...} } }
      let meetingData: any = null;

      if (json.ok && json.data) {
        // 중첩된 경우 다시 확인
        if (json.data.ok && json.data.data) {
          meetingData = json.data.data;
        } else if (json.data.id) {
          // 이미 meeting 객체인 경우
          meetingData = json.data;
        } else {
          meetingData = json.data;
        }
      } else if (json.id) {
        // 직접 meeting 객체인 경우
        meetingData = json;
      }

      // 안전한 기본값으로 보강
      if (meetingData) {
        meetingData.members = meetingData.members || { total: 0, preview: [] };
        meetingData.stats = meetingData.stats || {
          totalEvents: 0,
          monthlyParticipants: 0,
          totalPosts: 0,
        };
        meetingData.events = meetingData.events || [];
        meetingData.posts = meetingData.posts || [];
        meetingData.photos = meetingData.photos || [];
      }

      return { ok: true, data: meetingData };
    },
    enabled: !!meetingId,
  });

  const meeting = meetingData?.data;

  // 안전한 접근을 위한 기본값 설정 (이중 체크)
  // 모든 훅은 early return 이전에 호출되어야 함
  const members = meeting?.members || { total: 0, preview: [] };
  const stats = meeting?.stats || {
    totalEvents: 0,
    monthlyParticipants: 0,
    totalPosts: 0,
  };
  const events = Array.isArray(meeting?.events) ? meeting.events : [];
  const postsRaw = Array.isArray(meeting?.posts) ? meeting.posts : [];
  const posts = postsRaw.filter(
    (post): post is (typeof postsRaw)[number] & Record<string, any> =>
      Boolean(post) && typeof post === "object"
  );
  const rawPhotos = Array.isArray(meeting?.photos) ? meeting.photos : [];

  // useMemo를 early return 이전으로 이동 (훅 호출 순서 일관성 유지)
  const albumPhotos = useMemo(() => {
    if (!meeting) return [];
    const seen = new Set<string>();
    const addPhoto = (src?: string | null) => {
      if (!src) return;
      const normalized = normalizeImageUrl(src) || src;
      if (normalized) {
        seen.add(normalized);
      }
    };
    rawPhotos.forEach(addPhoto);
    posts.forEach((post) => {
      if (Array.isArray(post.images)) {
        post.images.forEach(addPhoto);
      }
    });
    return Array.from(seen);
  }, [meeting, rawPhotos, posts]);

  if (!match || !meetingId) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">
              유효하지 않은 모임 ID입니다
            </p>
            <Button variant="outline" onClick={() => setLocation("/meetings")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="animate-pulse space-y-6 p-4">
          <div className="h-12 bg-muted rounded" />
          <div className="h-64 bg-muted rounded" />
          <div className="h-32 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">
              모임 정보를 찾을 수 없습니다
            </p>
            <Button onClick={() => setLocation("/meetings")} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              목록으로 돌아가기
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // members와 stats의 내부 속성도 안전하게 처리
  const membersTotal = members?.total ?? 0;
  const membersPreview = Array.isArray(members?.preview) ? members.preview : [];
  const statsTotalEvents = stats?.totalEvents ?? 0;
  const statsTotalPosts = stats?.totalPosts ?? 0;

  const categoryLabel =
    meetingCategories.find((c) => c.id === meeting.category)?.label ||
    meeting.category;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}월 ${date.getDate()}`;
  };

  const formatTime = (timeString: string | null) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const period = hour >= 12 ? "오후" : "오전";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${period} ${displayHour}:${minutes}`;
  };

  // 모임 참여하기 핸들러
  const handleJoinMeeting = async () => {
    if (!user) {
      setAuthDialogOpen(true);
      return;
    }

    try {
      const token = await user.getIdToken();
      const res = await fetch(`/api/meetings/${meetingId}/join`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (res.ok && data.ok) {
        setIsJoined(true);
        // 모임 정보 다시 불러오기
        queryClient.invalidateQueries({
          queryKey: ["/api/meetings", meetingId],
        });
        // 성공 메시지
        if (data.alreadyJoined) {
          alert("이미 참여한 모임입니다.");
        } else {
          alert("모임에 참여했습니다!");
        }
      } else {
        // 400 오류인 경우 상세 메시지 표시
        const errorMessage =
          data.message || data.error || `참여하기 실패 (${res.status})`;
        console.error("Join meeting error:", { status: res.status, data });
        alert(errorMessage);
      }
    } catch (error) {
      console.error("Join meeting error:", error);
      alert("참여하기 중 오류가 발생했습니다.");
    }
  };

  const filteredPosts: Array<{
    id: string;
    title?: string;
    content: string;
    images: string[];
    authorNickname: string;
    authorLocation?: string;
    likes: number;
    comments: number;
    createdAt: string;
    category: string;
  }> =
    selectedPostCategory === "all"
      ? posts
      : posts.filter((p) => p.category === selectedPostCategory);

  return (
    <div className="min-h-screen bg-background">
      {/* 상단 헤더 (고정) */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/meetings")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold truncate flex-1 px-4 text-center">
            {meeting.title}
          </h1>
          <ShareKakaoButton
            path={`/meetings/${meeting.id}`}
            contentType="meeting"
            contentId={String(meeting.id)}
            title={meeting.title}
            description={meeting.description || undefined}
            ctaLabel="모임 보러가기"
            variant="ghost"
            size="sm"
            label=""
          />
          <Button variant="ghost" size="icon">
            <MoreVertical className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* 메인 레이아웃 */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* 좌측 사이드바 (데스크톱 전용) */}
          {!isMobile && meeting && (
            <MeetingSidebar
              meeting={{
                id: meeting.id,
                title: meeting.title,
                description: meeting.description || "",
                category: meeting.category || "",
                ageRange: meeting.ageRange || null,
                suburb: meeting.suburb || null,
                members: {
                  total: membersTotal,
                },
                stats: {
                  totalPosts: statsTotalPosts,
                },
              }}
              selectedPostCategory={selectedPostCategory}
              onPostCategoryChange={setSelectedPostCategory}
              onNavigateToSection={scrollToSection}
              onJoinMeeting={handleJoinMeeting}
              isJoined={isJoined}
            />
          )}

          {/* 메인 콘텐츠 */}
          <main className="flex-1 space-y-6 pb-24">
            {/* 앨범 섹션 */}
            {albumPhotos.length > 0 && (
              <Card ref={albumSectionRef} id="album-section">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-lg">앨범</CardTitle>
                  <Button variant="ghost" size="sm" asChild>
                    <Link href={`/meetings/${meetingId}/album`}>
                      더보기 <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {albumPhotos.slice(0, 6).map((photo, index) => (
                      <div
                        key={index}
                        className="aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() =>
                          setLocation(`/meetings/${meetingId}/album`)
                        }
                      >
                        <img
                          src={photo}
                          alt={`${meeting.title} 사진 ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {isMobile && meeting.ageRange && (
              <Card>
                <CardContent className="flex items-center justify-between py-4">
                  <div>
                    <p className="text-xs text-muted-foreground">권장 나이대</p>
                    <p className="text-base font-semibold">
                      {meeting.ageRange}
                    </p>
                  </div>
                  {!isJoined && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleJoinMeeting}
                    >
                      참여하기
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* 멤버 섹션 */}
            <Card ref={membersSectionRef} id="members-section">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-lg">멤버 {membersTotal}명</CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link href={`/meetings/${meetingId}/members`}>
                    더보기 <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {membersPreview.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <Avatar className="w-12 h-12 flex-shrink-0">
                        <AvatarImage src={getAvatarUrl(member) || undefined} />
                        <AvatarFallback>
                          {getDisplayName(member)?.[0] || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">
                            {getDisplayName(member)}
                          </span>
                          {member.role === "leader" && (
                            <>
                              <span className="text-xs text-muted-foreground">
                                방장
                              </span>
                              <span>👑</span>
                            </>
                          )}
                        </div>
                        {/* Phase 1: 닉네임/지역만 표시 */}
                        {(member.state || member.suburb) && (
                          <p className="text-sm text-muted-foreground">
                            {member.suburb || member.state || ""}
                            {member.suburb &&
                              member.state &&
                              `, ${member.state}`}
                          </p>
                        )}
                        {/* Phase 2에서 추가 예정: MBTI 표시 */}
                        {/* {member.mbti && (
                          <p className="text-xs text-muted-foreground">
                            {member.mbti}
                          </p>
                        )} */}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 일정 섹션 - 항상 표시 */}
            <Card ref={eventsSectionRef} id="events-section">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-lg">
                  일정 {statsTotalEvents}개
                </CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link href={`/meetings/${meetingId}/events`}>
                    더보기 <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </CardHeader>
              <CardContent>
                {events.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {events.slice(0, 6).map((event) => (
                      <div
                        key={event.id}
                        className="rounded-lg border overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                      >
                        {event.image && (
                          <div className="aspect-video bg-muted overflow-hidden">
                            <img
                              src={event.image}
                              alt={event.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="p-3">
                          <div className="flex items-start justify-between mb-2">
                            <span className="text-xs text-muted-foreground">
                              {formatDate(event.date)}
                            </span>
                            <Badge
                              variant={
                                event.status === "open"
                                  ? "default"
                                  : "secondary"
                              }
                              className="text-xs"
                            >
                              {event.status === "open" ? "모집중" : "마감"}
                            </Badge>
                          </div>
                          <h3 className="font-semibold mb-2 line-clamp-2">
                            {event.isPrivate
                              ? "🔒 모임에만 공개된 일정이에요."
                              : event.title}
                          </h3>
                          {event.time && (
                            <p className="text-xs text-muted-foreground mb-1">
                              {formatTime(event.time)}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            참가자 {event.attendees}
                            {event.capacity ? ` / ${event.capacity}` : ""}명
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <p className="text-sm mb-4">아직 등록된 일정이 없습니다</p>
                    {user &&
                      meeting &&
                      (meeting.hostId === user.uid ||
                        meeting.hostEmail === user.email) && (
                        <Button
                          variant="outline"
                          onClick={() =>
                            navigate(`/meetings/${meetingId}/events/new`)
                          }
                        >
                          일정 만들기
                        </Button>
                      )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 게시글 섹션 */}
            <Card ref={postsSectionRef} id="posts-section">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-lg">
                  게시글 {statsTotalPosts}개
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (!user) {
                        setAuthDialogOpen(true);
                      } else {
                        navigate(`/meetings/${meetingId}/posts/new`);
                      }
                    }}
                  >
                    글쓰기
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link href={`/meetings/${meetingId}/posts`}>
                      더보기 <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* 탭 필터 */}
                <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide mb-4">
                  {boardCategories.map((cat) => (
                    <Button
                      key={cat.id}
                      variant={
                        selectedPostCategory === cat.id
                          ? "default"
                          : "secondary"
                      }
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => setSelectedPostCategory(cat.id)}
                    >
                      {"emoji" in cat && cat.emoji && (
                        <span className="mr-1">{cat.emoji}</span>
                      )}
                      {cat.label}
                      {"emoji" in cat && cat.emoji && (
                        <span className="ml-1">{cat.emoji}</span>
                      )}
                    </Button>
                  ))}
                </div>

                {/* 게시글 리스트 */}
                {filteredPosts.length > 0 ? (
                  <div className="space-y-4">
                    {filteredPosts.slice(0, 3).map((post) => {
                      const hasImages =
                        Array.isArray(post.images) && post.images.length > 0;
                      const thumbnail = hasImages
                        ? normalizeImageUrl(post.images[0]) || post.images[0]
                        : null;
                      return (
                        <div
                          key={post.id}
                          className="p-4 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() =>
                            setLocation(
                              `/meetings/${meetingId}/posts/${post.id}`
                            )
                          }
                        >
                          <div className="flex gap-4">
                            <div className="flex-1">
                              <p className="text-sm mb-3 line-clamp-3">
                                {post.content}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                <span className="font-medium">
                                  {post.authorNickname}
                                  {post.authorLocation &&
                                    ` (${post.authorLocation})`}
                                </span>
                                <span>·</span>
                                <span>
                                  {post.createdAt
                                    ? formatDistanceToNow(
                                        new Date(post.createdAt),
                                        {
                                          addSuffix: true,
                                          locale: ko,
                                        }
                                      )
                                    : "방금 전"}
                                </span>
                                <span>·</span>
                                <span>
                                  {
                                    boardCategories.find(
                                      (c) => c.id === post.category
                                    )?.label
                                  }
                                </span>
                              </div>
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <span>👍</span>
                                  <span>{post.likes || 0}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span>💬</span>
                                  <span>{post.comments || 0}</span>
                                </div>
                              </div>
                            </div>
                            {thumbnail && (
                              <div className="w-24 h-24 rounded-lg overflow-hidden border shrink-0">
                                <img
                                  src={thumbnail}
                                  alt={`${post.authorNickname} 게시글 이미지`}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>아직 게시글이 없습니다</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </main>
        </div>
      </div>

      {/* 하단 고정 참여바 (모바일 전용) */}
      {isMobile && (
        <MeetingBottomBar
          isFavorite={isFavorite}
          isJoined={isJoined}
          onFavoriteToggle={() => setIsFavorite(!isFavorite)}
          onJoinMeeting={handleJoinMeeting}
          ageRange={meeting.ageRange || null}
        />
      )}

      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
    </div>
  );
}
